import { CartAddEvent } from '@theme/events';

/** Hero look selection uses the same cart section/event contract as Fabric's native product form. */
class RsHero extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('[data-hero-form]');
    this.items = [...this.querySelectorAll('[data-hero-item]')];
    this.status = this.querySelector('[data-hero-status]');
    this.video = this.querySelector('.rs-hero__media video');
    this.videoButton = this.querySelector('[data-hero-video]');
    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.videoPausedByUser = this.reducedMotion.matches;
    this.isVisible = false;

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-hero-open]');
      if (opener) this.open(opener.dataset.heroOpen, opener);
      if (event.target.closest('[data-hero-close]')) this.dialog?.close();
      if (event.target === this.dialog) {
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      }
      if (event.target.closest('[data-hero-video]') && this.video) {
        this.videoPausedByUser = !this.video.paused;
        this.syncVideo();
      }
    }, { signal });
    this.dialog?.addEventListener('close', () => {
      this.opener?.focus({ preventScroll: true });
      this.syncVideo();
    }, { signal });
    this.form?.addEventListener('submit', (event) => this.submit(event), { signal });
    this.form?.addEventListener('change', (event) => {
      const item = event.target.closest('[data-hero-item]');
      if (!item) return;
      if (event.target.matches('[data-hero-variant]')) {
        const option = event.target.selectedOptions[0];
        const check = item.querySelector('[data-hero-check]');
        if (check && option?.value) check.checked = true;
        if (option?.dataset.priceLabel) item.querySelector('[data-hero-price]').textContent = option.dataset.priceLabel;
        const img = item.querySelector('.rs-hero__product-img');
        // Do not leave a previous colour visible when the selected variant has no assigned image.
        if (img) {
          img.hidden = !option?.dataset.image;
          if (option?.dataset.image) { img.src = option.dataset.image; img.removeAttribute('srcset'); }
        }
      }
      this.updateTotal();
    }, { signal });
    document.addEventListener('visibilitychange', () => this.syncVideo(), { signal });
    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) this.videoPausedByUser = true;
      this.syncVideo();
    }, { signal });
    document.addEventListener('shopify:block:select', (event) => {
      if (!this.contains(event.target)) return;
      const hotspot = event.target.matches('[data-hero-open]') ? event.target : event.target.querySelector('[data-hero-open]');
      if (hotspot) this.open(hotspot.dataset.heroOpen, hotspot);
    }, { signal });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) this.dialog?.close();
    }, { signal });
    if (this.video) {
      this.videoButton.hidden = false;
      this.video.addEventListener('play', () => this.renderVideoButton(), { signal });
      this.video.addEventListener('pause', () => this.renderVideoButton(), { signal });
      this.video.addEventListener('error', () => { this.video.classList.remove('is-playing'); }, { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        this.syncVideo();
      }, { threshold: .05 });
      this.observer.observe(this);
    }
    this.updateTotal();
  }
  open(id, opener) {
    if (!this.dialog || this.busy) return;
    this.opener = opener;
    this.items.forEach((item) => {
      item.hidden = id !== 'all' && item.dataset.heroItem !== id;
      const select = item.querySelector('[data-hero-variant]');
      const check = item.querySelector('[data-hero-check]');
      if (select) select.required = !item.hidden && !select.disabled && Boolean(check?.checked);
    });
    this.querySelector('[data-hero-title]').textContent = id === 'all' ? 'Shop the look' : 'Product details';
    this.status.textContent = '';
    this.updateTotal();
    if (!this.dialog.open) this.dialog.showModal();
    this.syncVideo();
  }
  updateTotal() {
    let total = 0;
    let available = 0;
    let selectedCount = 0;
    for (const item of this.items) {
      const select = item.querySelector('[data-hero-variant]');
      const check = item.querySelector('[data-hero-check]');
      if (!select || !check) continue;
      select.required = !item.hidden && check.checked && !check.disabled;
      if (item.hidden || !check.checked || check.disabled) continue;
      available += 1;
      const option = select.selectedOptions[0];
      if (select.value && !option.disabled) {
        total += Number(option.dataset.price || 0) * (Number(option.dataset.quantity) || 1);
        selectedCount += 1;
      }
    }
    let formatted = '—';
    if (selectedCount) {
      try { formatted = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(total / 100); }
      catch { formatted = (total / 100).toFixed(2); }
    }
    this.querySelector('[data-hero-total]').textContent = formatted;
    this.form.querySelector('[type="submit"]').disabled = this.busy || !available;
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy || !this.form.reportValidity()) return;
    const items = [];
    for (const item of this.items) {
      if (item.hidden || !item.querySelector('[data-hero-check]')?.checked) continue;
      const select = item.querySelector('[data-hero-variant]');
      const option = select?.selectedOptions[0];
      if (!select?.value || select.disabled || option.disabled) continue;
      const quantity = Number(option.dataset.quantity) || 1;
      const existing = items.find((row) => row.id === select.value);
      if (existing) existing.quantity += quantity;
      else items.push({ id: select.value, quantity });
    }
    if (!items.length) { this.status.textContent = 'Choose an available option first.'; return; }
    this.busy = true;
    this.updateTotal();
    this.status.textContent = 'Adding your selection…';
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    let payload;
    let failure;
    try {
      const response = await fetch(this.dataset.cartAdd, {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }),
      });
      payload = await response.json();
      if (!response.ok || payload.status) failure = typeof payload.description === 'string' ? payload.description : 'This selection could not be added. Please check available stock.';
    } catch { failure = 'The result could not be confirmed. Check your cart before trying again.'; }
    let cart;
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (response.ok) cart = await response.json();
    } catch { /* A returned section still lets Fabric reconcile its drawer. */ }
    if (!failure) this.dialog.close();
    // Stock-limit failures may still change Shopify's cart; reconcile even on an error.
    if (cart || payload?.sections || !failure) {
      document.dispatchEvent(new CartAddEvent(cart, this.id, {
        source: cart ? 'rs-hero' : 'product-form-component',
        itemCount: cart ? cart.item_count : failure ? 0 : items.reduce((sum, item) => sum + item.quantity, 0),
        didError: Boolean(failure), sections: payload?.sections || {},
      }));
    }
    this.status.textContent = failure || 'Your selection was added to the cart.';
    this.busy = false;
    this.updateTotal();
  }
  syncVideo() {
    if (!this.video) return;
    if (!this.isVisible || document.hidden || this.dialog?.open || this.videoPausedByUser) this.video.pause();
    else this.video.play().catch(() => { this.videoPausedByUser = true; this.renderVideoButton(); });
    this.renderVideoButton();
  }
  renderVideoButton() {
    if (!this.video || !this.videoButton) return;
    this.video.classList.toggle('is-playing', !this.video.paused);
    this.videoButton.setAttribute('aria-label', this.video.paused ? 'Play background video' : 'Pause background video');
    this.videoButton.firstElementChild.textContent = this.video.paused ? '▶' : 'Ⅱ';
  }
  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.dialog?.close();
    this.video?.pause();
  }
}
if (!customElements.get('rs-hero')) customElements.define('rs-hero', RsHero);
